import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gradeBenchPanelActions } from '@codametrix/ui-common';
import './styles/index.scss';
export type EvaluateWorkflowFn = (
  request: CQA.WorkflowEvaluationRequest,
) => Promise<CQA.WorkflowEvaluationResponse>;

export interface UseWorkflowEvaluationParams {
  workflowId: number | undefined;
  attributes: CQA.Attribute[];
  workflowQuestions?: CQA.WorkflowQuestion[];
  codeContext?: CQA.WorkflowEvaluationRequest['codeContext'];
  workItemId?: number;
  evaluateFn: EvaluateWorkflowFn;
  enabled?: boolean;
  /** Rubric version ID — enables cross-workflow autofill via WorkflowTriggers */
  rubricVersionId?: number;
  /** Case-level attributes for cross-workflow autofill */
  caseAttributes?: CQA.Attribute[];
  /** Source attributes that can affect cross-workflow trigger visibility but are resolved server-side. */
  triggerSourceAttributes?: CQA.Attribute[];
  /** Source SPANS attribute when evaluating source-scoped SPAN workflow questions directly. */
  spanScopeAttributeId?: number;
  /** @deprecated use spanScopeAttributeId. */
  parentQuestionId?: number;
  /** Active span annotation id when evaluating source-scoped SPAN questions directly. */
  spanId?: number;
  /** Owning code entity id when evaluating code-attached source-scoped SPAN questions. */
  codeId?: string;
}

export interface UseWorkflowEvaluationResult {
  /** Raw API response (null until first successful response) */
  evaluation: CQA.WorkflowEvaluationResponse | null;
  /** Visible non-SYSTEM workflow questions, sorted by sequenceOrder */
  visibleQuestions: CQA.WorkflowQuestion[];
  /** Filtered answer IDs per question (from SET_ANSWERS triggers) */
  filteredAnswersMap: Map<number, number[]>;
  /** Auto-filled answer IDs per question (from AUTOFILL triggers) */
  autofilledAnswersMap: Map<number, number[]>;
  /** Whether the case-level workflow is complete (all required questions answered) */
  caseLevelComplete: boolean | null;
  isLoading: boolean;
  /**
   * True only when the last completed evaluation reflects the current attributes.
   */
  isSettled: boolean;
  error: Error | null;
}

/**
 * Calls the backend workflow evaluation API and returns pre-mapped results
 * ready for rendering. Shows loading state during backend evaluation.
 */
export function useWorkflowEvaluation({
  workflowId,
  attributes,
  workflowQuestions,
  codeContext,
  workItemId,
  evaluateFn,
  enabled = true,
  rubricVersionId,
  caseAttributes,
  triggerSourceAttributes,
  spanScopeAttributeId,
  parentQuestionId,
  spanId,
  codeId,
}: UseWorkflowEvaluationParams): UseWorkflowEvaluationResult {
  const dispatch = useDispatch();
  const sdCaseId = useSelector(
    (state: any) => state.gradeBenchPanel?.invoices?.[0]?.sdCaseId
  ) as string | undefined;
  const [evaluation, setEvaluation] = useState<CQA.WorkflowEvaluationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && !!workflowId);
  const [error, setError] = useState<Error | null>(null);
  // Tracks the attributesKey of the last *completed* evaluation. isSettled is true
  // only when this matches the current attributesKey, meaning the visible questions
  // in the response actually reflect the current answer state. Gates completion
  // checks so we never fire reportCompletion based on stale visibleQuestions.
  const [settledAttributesKey, setSettledAttributesKey] = useState('');

  // Stabilize evaluateFn so callers don't need to memoize it
  const evaluateFnRef = useRef(evaluateFn);
  evaluateFnRef.current = evaluateFn;

  // Serialize attributes to a stable string so the effect only re-fires on value changes
  const attributesKey = useMemo(
    () => JSON.stringify(attributes.map(a => `${a.questionId}:${a.answerValueId ?? a.spanAnnotationIds?.join(',') ?? a.answerDisplayText}`)),
    [attributes],
  );

  // Serialize context objects for stable dependency comparison
  const codeContextKey = useMemo(
    () => (codeContext ? JSON.stringify(codeContext) : ''),
    [codeContext],
  );

  const caseAttributesKey = useMemo(
    () => caseAttributes ? JSON.stringify(caseAttributes.map(a => `${a.questionId}:${a.answerValueId}`)) : '',
    [caseAttributes],
  );

  const triggerSourceAttributesKey = useMemo(
    () => triggerSourceAttributes ? JSON.stringify(triggerSourceAttributes.map(a => `${a.questionId}:${a.answerValueId ?? a.spanAnnotationIds?.join(',') ?? a.answerDisplayText}`)) : '',
    [triggerSourceAttributes],
  );
  useEffect(() => {
    if (!enabled || !workflowId) {
      setIsLoading(false);
      setEvaluation(null);
      setError(null);
      setSettledAttributesKey('');
      return;
    }

    // Use a local variable instead of a shared ref for cancellation.
    // Each effect invocation gets its own `cancelled` flag so that when
    // a newer invocation's cleanup marks a previous one as cancelled,
    // it doesn't get reset by the newer invocation (which was the bug
    // with the shared cancelledRef — the new effect would set
    // cancelledRef.current = false, un-cancelling the stale in-flight request).
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    // Debounce evaluation to avoid rapid-fire API calls when attributes change quickly
    // (e.g., multiple downstream clears followed by the new answer save).
    const timerId = setTimeout(() => {
      const request: CQA.WorkflowEvaluationRequest = {
        workflowId,
        attributes: attributes
          .filter(a => a.answerValueId != null || (a.spanAnnotationIds != null && a.spanAnnotationIds.length > 0) || (a.answerDisplayText != null && a.answerDisplayText !== ''))
          .map((a) => ({
            questionId: a.questionId,
            ...(a.answerValueId != null ? { answerValueId: a.answerValueId } : {}),
            ...(a.spanAnnotationIds != null && a.spanAnnotationIds.length > 0 ? { spanAnnotationIds: a.spanAnnotationIds } : {}),
            ...(a.answerDisplayText != null && a.answerDisplayText !== '' ? { answerDisplayText: a.answerDisplayText } : {}),
          })),
        codeContext,
        workItemId,
        sdCaseId,
        rubricVersionId,
        spanScopeAttributeId: spanScopeAttributeId ?? parentQuestionId,
        spanId,
        codeId,
        // WorkflowTriggers only apply when the case source answer is a concrete selected option.
        caseAttributes: caseAttributes
          ?.filter((a) => a.answerValueId != null)
          .map((a) => ({
            questionId: a.questionId,
            answerValueId: a.answerValueId,
          })),
      };

      const keyForThisRequest = attributesKey;
      evaluateFnRef.current(request)
        .then((result) => {
          if (!cancelled) {
            setEvaluation(result);
            setSettledAttributesKey(keyForThisRequest);
            if (result.validation) {
              dispatch(gradeBenchPanelActions.setValidationResult(result.validation));
            }
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setError(e instanceof Error ? e : new Error(String(e)));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
    // Deps use serialized keys (attributesKey, etc.) for stable identity instead of object refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, attributesKey, codeContextKey, workItemId, enabled, rubricVersionId, caseAttributesKey, triggerSourceAttributesKey, spanScopeAttributeId, parentQuestionId, spanId, codeId]);

  // Build question lookup map
  const questionMap = useMemo(() => {
    const map = new Map<number, CQA.WorkflowQuestion>();
    workflowQuestions?.forEach(wq => map.set(wq.question.id, wq));
    return map;
  }, [workflowQuestions]);

  // Map visible questions from API response to workflow question objects
  const visibleQuestions = useMemo((): CQA.WorkflowQuestion[] => {
    if (!evaluation?.visibleQuestions?.length) return [];
    return [...evaluation.visibleQuestions]
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      .map(vq => questionMap.get(vq.questionId))
      .filter((wq): wq is CQA.WorkflowQuestion => wq != null && wq.question.questionType !== 'SYSTEM');
  }, [evaluation?.visibleQuestions, questionMap]);

  // Convert filteredAnswers Record to Map
  const filteredAnswersMap = useMemo((): Map<number, number[]> => {
    if (!evaluation?.filteredAnswers) return new Map();
    return new Map(
      Object.entries(evaluation.filteredAnswers).map(([k, v]) => [Number(k), v])
    );
  }, [evaluation?.filteredAnswers]);

  // Convert autofilledAnswers Record to Map
  const autofilledAnswersMap = useMemo((): Map<number, number[]> => {
    if (!evaluation?.autofilledAnswers) return new Map();
    return new Map(
      Object.entries(evaluation.autofilledAnswers).map(([k, v]) => [Number(k), v])
    );
  }, [evaluation?.autofilledAnswers]);

  return {
    evaluation,
    visibleQuestions,
    filteredAnswersMap,
    autofilledAnswersMap,
    // caseLevelComplete is null when: (a) no CASE workflow exists, (b) API hasn't responded yet,
    // or (c) the API errored. Gating uses `=== false`, so null is intentionally fail-open:
    // code/span questions remain enabled when the evaluation API is unavailable or not yet returned.
    caseLevelComplete: evaluation?.caseLevelComplete ?? null,
    isLoading,
    isSettled: !isLoading && settledAttributesKey === attributesKey,
    error,
  };
}

export default useWorkflowEvaluation;
